/**
 * community/supportApi.ts — the browser's client for the RAISER's side of support requests
 * (CH-F13 / FB-F15): raising one, and reading your own.
 *
 * WHY A MODULE OF ITS OWN rather than more of `community/api.ts`: these two calls are not community
 * routes. The write lands on `/api/support/requests` (routes/support.ts, the triage family's one
 * open-to-everyone endpoint) and the read on `/api/me/support-requests` — "`/api/me` is the ONLY
 * route family that returns PRIVATE content" (routes/index.ts). Keeping them here means the community
 * client keeps describing `/api/community` alone. The fetch/normalise plumbing is reused from that
 * module unchanged: there is one place in this slice that turns a Response into a result union, and a
 * second copy of it is how a 204 or a malformed body starts being handled two different ways.
 *
 * The shapes below MIRROR THE SERVER BY HAND (packages/api/src/db/support.ts). They are not
 * generated, so a server-side change here compiles clean on both sides and silently drops a field —
 * `status` is therefore taken from `@trailblazer/shared`'s SUPPORT_REQUEST_STATUSES rather than
 * re-typed, which is one fewer thing that can drift.
 *
 * THE RAISER IS NEVER SENT. `POST /api/support/requests` derives it from the session
 * (`requestUserId`), and the body schema has no user field to put one in — so there is no way, from
 * here, to file a request in a colleague's name.
 */
import type { SupportRequestStatus } from '@trailblazer/shared';
import { apiGet, apiPost, type ApiResult } from './api';

/**
 * The body bounds, measured AFTER trim exactly as the server measures them
 * (`SUPPORT_REQUEST_BODY_MIN_LENGTH`/`MAX` in `db/support.ts`, applied by the route's
 * `z.string().trim().min().max()`). Mirrored here only so the form can disable its own button and say
 * why before a round trip; the server remains the authority and a 400 is still handled.
 */
export const SUPPORT_BODY_MIN_LENGTH = 10;
export const SUPPORT_BODY_MAX_LENGTH = 2000;

/** One recorded status change as the raiser sees it (db/support.ts MySupportRequestChange). The
 *  triager's REASON is here; the triager's NAME deliberately is not (FB-F16, NT-F17). */
export interface MySupportRequestChange {
  fromStatus: SupportRequestStatus | null;
  toStatus: SupportRequestStatus;
  reason: string;
  changedAt: string;
}

/** One of the caller's own support requests (db/support.ts MySupportRequest). `toolName` is null for
 *  an ask-for-help request, which never carries a tool. `history` is oldest-first and empty until a
 *  triager moves it. */
export interface MySupportRequest {
  requestId: string;
  origin: 'tool_report' | 'ask_for_help';
  toolId: string | null;
  toolName: string | null;
  body: string;
  status: SupportRequestStatus;
  createdAt: string;
  history: MySupportRequestChange[];
}

/** What `POST /api/support/requests` answers with on 201 — the row the DB actually wrote, so the
 *  status shown back to the raiser is the one the 0007 CHECK allowed and never a constant here. */
export interface CreatedSupportRequest {
  requestId: string;
  status: SupportRequestStatus;
  createdAt: string;
}

/**
 * Raise a general "ask for help" request (CH-F13). Sends `{origin, body}` and NOTHING ELSE.
 *
 * NO `toolId` IS SENT, and that is deliberate rather than an omission: `createSupportRequestWith`
 * (db/support.ts:126) forces `tool_id` to NULL for `ask_for_help` whatever the caller supplies, so a
 * tool id sent from here would be accepted, silently discarded, and leave the triager looking at a
 * request that claims a tool it no longer names. Help with ONE named tool is the other origin
 * (`tool_report`), which belongs on the tool's own page.
 */
export async function postAskForHelp(body: string): Promise<ApiResult<CreatedSupportRequest>> {
  return apiPost<CreatedSupportRequest>('/api/support/requests', {
    origin: 'ask_for_help',
    body: body.trim(),
  });
}

/** The caller's OWN support requests, newest first, at all four statuses including Closed (PR-F35). */
export async function getMySupportRequests(): Promise<ApiResult<{ requests: MySupportRequest[] }>> {
  return apiGet<{ requests: MySupportRequest[] }>('/api/me/support-requests');
}
